import type { TypeWindow } from 'geoview-core';
import { MuiColorInput } from 'mui-color-input';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import {
  useStoreDrawerStyle,
  useStoreDrawerActiveGeom,
  useStoreDrawerSelectedDrawingType,
} from 'geoview-core/core/stores/states/drawer-state';
import { useStoreAppDisplayLanguage } from 'geoview-core/core/stores/states/app-state';
import { useDrawerController } from 'geoview-core/core/controllers/use-controllers';
import { useTranslation } from 'geoview-core/core/translation/i18n';
import { logger } from 'geoview-core/core/utils/logger';
import type { Theme, SxStyles } from 'geoview-core/ui/style/types';
import { geoViewColors as defaultGeoViewColors, GEOVIEW_SHAPE } from 'geoview-core/ui/style/default';
import { getFocusIndicatorStyles } from 'geoview-core/ui/style/themeOptionsGenerator';

import { FONT_OPTIONS, DEFAULT_FONT, loadGoogleFont } from '../utils/fonts';

/**
 * Builds the style panel's sx classes.
 *
 * @param theme - The MUI theme
 * @returns The sx classes object
 */
const getSxClasses = (theme: Theme): SxStyles => ({
  listItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  label: {
    marginBottom: theme.spacing(0.25),
  },
  input: {
    width: '100%',
  },
  numberInput: {
    width: '100%',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadiusSm,
  },
  row: {
    display: 'flex',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  rowItem: {
    flex: 1,
  },
  formattingRow: {
    display: 'flex',
    gap: theme.spacing(0.25),
    width: '100%',
  },
  formatButton: {
    width: 40,
    height: 40,
    borderRadius: theme.shape.borderRadiusFull,
    '&.Mui-focusVisible': {
      ...getFocusIndicatorStyles(theme.palette.geoViewColor ?? defaultGeoViewColors),
      boxShadow: 'none',
      outlineOffset: 0,
    },
  },
});

/**
 * Renders the style button icon.
 *
 * @returns The palette icon element
 */
export function StyleButton(): JSX.Element {
  // Log
  logger.logTraceRender('geoview-drawer/src/buttons/style > StyleButton');

  const { PaletteIcon } = (window as TypeWindow).cgpv.ui.elements;

  return <PaletteIcon />;
}

/**
 * Creates a style panel to customize drawing styles.
 *
 * @returns The style panel element
 */
export function StylePanel(): JSX.Element {
  // Log
  logger.logTraceRender('geoview-drawer/src/buttons/style');

  const { cgpv } = window as TypeWindow;
  const { ui, reactUtilities } = cgpv;
  const { useCallback, useEffect, useMemo } = reactUtilities.react;
  const { useTheme } = ui;
  const { t } = useTranslation<string>();

  // Components
  const { Box, List, ListItem, Typography, TextField, IconButton, FormatBoldIcon, FormatItalicIcon } = ui.elements;

  const theme = useTheme();
  const memoSxClasses = useMemo(() => getSxClasses(theme), [theme]);

  // Get store values
  const style = useStoreDrawerStyle();
  const activeGeom = useStoreDrawerActiveGeom();
  const selectedDrawingType = useStoreDrawerSelectedDrawingType();
  const displayLanguage = useStoreAppDisplayLanguage();
  const drawerController = useDrawerController();

  /** The current geometry type, using the selected drawing type or active geometry as fallback. */
  const currentGeomType = selectedDrawingType ?? activeGeom;

  // Local state for color inputs
  const [localFillColor, setLocalFillColor] = useState<string>(style.fillColor);
  const [localStrokeColor, setLocalStrokeColor] = useState<string>(style.strokeColor);
  const [localTextColor, setLocalTextColor] = useState<string>(style.textColor || '#000000');
  const [localTextHaloColor, setLocalTextHaloColor] = useState<string>(style.textHaloColor || 'rgba(255,255,255,0.8)');

  // #region Handlers

  /**
   * Handles when the user changes the text value
   */
  const handleTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      drawerController.setTextValue(event.target.value);
    },
    [drawerController]
  );

  /**
   * Handles when the user changes the font
   */
  const handleFontChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const selectedFont = event.target.value;
      const fontOption = FONT_OPTIONS.find((f) => f.value === selectedFont);

      // Load Google Font if needed
      if (fontOption?.isGoogleFont) {
        loadGoogleFont(fontOption.name);
      }

      drawerController.setTextFont(selectedFont);
    },
    [drawerController]
  );

  /**
   * Handles when the user changes the text size
   */
  const handleTextSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const size = parseInt(event.target.value, 10);
      if (!Number.isNaN(size)) drawerController.setTextSize(size);
    },
    [drawerController]
  );

  /**
   * Handles when the user changes the text halo width
   */
  const handleTextHaloWidthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const size = parseInt(event.target.value, 10);
      if (!Number.isNaN(size)) drawerController.setTextHaloWidth(size);
    },
    [drawerController]
  );

  /**
   * Handles when the user changes the fill color
   */
  const handleFillColorChange = useCallback((newFillColor: string): void => {
    setLocalFillColor(newFillColor);
  }, []);

  /**
   * Handles when the fill color picker closes
   */
  const handleFillColorClose = useCallback((): void => {
    drawerController.setFillColor(localFillColor);
  }, [drawerController, localFillColor]);

  /**
   * Handles when the user changes the stroke color
   */
  const handleStrokeColorChange = useCallback((newStrokeColor: string): void => {
    setLocalStrokeColor(newStrokeColor);
  }, []);

  /**
   * Handles when the stroke color picker closes
   */
  const handleStrokeColorClose = useCallback((): void => {
    drawerController.setStrokeColor(localStrokeColor);
  }, [drawerController, localStrokeColor]);

  /**
   * Handles when the user changes the icon size
   */
  const handleIconSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const size = parseInt(event.target.value, 10);
      if (!Number.isNaN(size)) drawerController.setDrawerIconSize(size);
    },
    [drawerController]
  );

  /**
   * Handles when the user changes the text color
   */
  const handleTextColorChange = useCallback((newColor: string): void => {
    setLocalTextColor(newColor);
  }, []);

  /**
   * Handles when the text color picker closes
   */
  const handleTextColorClose = useCallback((): void => {
    drawerController.setTextColor(localTextColor);
  }, [drawerController, localTextColor]);

  /**
   * Handles when the user changes the text halo color
   */
  const handleTextHaloColorChange = useCallback((newColor: string): void => {
    setLocalTextHaloColor(newColor);
  }, []);

  /**
   * Handles when the text halo color picker closes
   */
  const handleTextHaloColorClose = useCallback((): void => {
    drawerController.setTextHaloColor(localTextHaloColor);
  }, [drawerController, localTextHaloColor]);

  /**
   * Handles when the user changes the stroke width
   */
  const handleStrokeWidthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const width = parseFloat(event.target.value);
      if (Number.isNaN(width)) return;
      drawerController.setStrokeWidth(width);
    },
    [drawerController]
  );

  /**
   * Handles when the user toggles bold formatting
   */
  const handleToggleBold = useCallback((): void => {
    drawerController.setTextBold(!style.textBold);
  }, [drawerController, style.textBold]);

  /**
   * Handles when the user toggles italic formatting
   */
  const handleToggleItalic = useCallback((): void => {
    drawerController.setTextItalic(!style.textItalic);
  }, [drawerController, style.textItalic]);

  // #endregion

  // Add close button to MUIColorInputs
  useEffect(() => {
    logger.logTraceUseEffect('STYLE PANEL - Color picker close button setup', displayLanguage);

    const addCloseButtons = (): void => {
      // Find all color picker popovers
      // MuiColorInput renders its popover in a portal on document.body, outside any map's subtree,
      // so a map-scoped wrapper cannot reach it — a global query is required here.
      // eslint-disable-next-line no-restricted-syntax
      const popovers = document.querySelectorAll('.MuiColorInput-PopoverBody');

      popovers.forEach((popover) => {
        // Check if we already added a close button
        if (popover.parentElement?.querySelector('.color-picker-close-btn')) return;

        // Create header div
        const header = document.createElement('div');
        header.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0px 0px 8px 8px;
        font-size: 14px;
        font-weight: 500;
      `;
        header.textContent = t('drawer.colourPicker');

        // Create close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'color-picker-close-btn';
        closeBtn.innerHTML = '✕';
        closeBtn.style.cssText = `
        background: none;
        border: none;
        font-size: 16px;
        cursor: pointer;
        padding: 4px;
        border-radius: ${GEOVIEW_SHAPE.borderRadiusFull};
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

        closeBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Find the specific popover and close it by clicking outside
          const popoverRoot = popover.closest('.MuiPopover-root');
          if (popoverRoot) {
            const backdrop = popoverRoot.querySelector('.MuiBackdrop-root');
            if (backdrop) {
              (backdrop as HTMLElement).click();
            }
          }
        };

        header.appendChild(closeBtn);

        // Insert header before the popover body
        if (popover.parentElement) {
          popover.parentElement.insertBefore(header, popover);
        }
      });
    };

    // Use MutationObserver to detect when popovers are added
    const observer = new MutationObserver(addCloseButtons);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [displayLanguage, t]);

  // Preload all Google Fonts
  useEffect(() => {
    logger.logTraceUseEffect('STYLE PANEL - Preload Google Fonts');

    FONT_OPTIONS.forEach((font) => {
      if (font.isGoogleFont) {
        loadGoogleFont(font.name);
      }
    });
  }, []);

  return (
    <List>
      {/* Text-specific controls */}
      {currentGeomType === 'Text' && (
        <>
          <ListItem sx={memoSxClasses.listItem}>
            <Typography variant="subtitle2" sx={memoSxClasses.label}>
              {t('drawer.text')}
            </Typography>
            <TextField value={style.text || ''} onChange={handleTextChange} sx={memoSxClasses.input} placeholder="Enter text" multiline />
          </ListItem>

          <ListItem sx={memoSxClasses.listItem}>
            <Typography variant="subtitle2" sx={memoSxClasses.label}>
              {t('drawer.textFont')}
            </Typography>
            <TextField
              select
              value={style.textFont || DEFAULT_FONT}
              onChange={handleFontChange}
              sx={{
                ...memoSxClasses.input,
                '& .MuiNativeSelect-select': {
                  fontFamily: style.textFont || DEFAULT_FONT,
                },
              }}
              slotProps={{
                select: {
                  native: true,
                },
              }}
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                  {font.name}
                </option>
              ))}
            </TextField>
          </ListItem>

          {/* Text Color and Size in one row */}
          <ListItem sx={memoSxClasses.listItem}>
            <Box sx={memoSxClasses.row}>
              <Box sx={memoSxClasses.rowItem}>
                <Typography variant="subtitle2" sx={memoSxClasses.label}>
                  {t('drawer.textColour')}
                </Typography>
                <MuiColorInput
                  value={localTextColor}
                  onChange={handleTextColorChange}
                  onBlur={handleTextColorClose}
                  sx={memoSxClasses.input}
                />
              </Box>
              <Box sx={memoSxClasses.rowItem}>
                <Typography variant="subtitle2" sx={memoSxClasses.label}>
                  {t('drawer.textSize')}
                </Typography>
                <TextField
                  value={style.textSize || 14}
                  onChange={handleTextSizeChange}
                  sx={memoSxClasses.input}
                  slotProps={{
                    input: {
                      type: 'number',
                      inputProps: { min: 4, max: 100, step: 1 },
                    },
                  }}
                />
              </Box>
            </Box>
          </ListItem>

          {/* Halo Color and Size in one row */}
          <ListItem sx={memoSxClasses.listItem}>
            <Box sx={memoSxClasses.row}>
              <Box sx={memoSxClasses.rowItem}>
                <Typography variant="subtitle2" sx={memoSxClasses.label}>
                  {t('drawer.textHaloColour')}
                </Typography>
                <MuiColorInput
                  value={localTextHaloColor}
                  onChange={handleTextHaloColorChange}
                  onBlur={handleTextHaloColorClose}
                  sx={memoSxClasses.input}
                />
              </Box>
              <Box sx={memoSxClasses.rowItem}>
                <Typography variant="subtitle2" sx={memoSxClasses.label}>
                  {t('drawer.textHaloWidth')}
                </Typography>
                <TextField
                  value={style.textHaloWidth}
                  onChange={handleTextHaloWidthChange}
                  sx={memoSxClasses.input}
                  slotProps={{
                    input: {
                      type: 'number',
                      inputProps: { min: 0, max: 100, step: 1 },
                    },
                  }}
                />
              </Box>
            </Box>
          </ListItem>
          <ListItem sx={memoSxClasses.listItem}>
            <Typography variant="subtitle2" sx={memoSxClasses.label}>
              {t('drawer.textFormatting')}
            </Typography>
            <Box sx={memoSxClasses.formattingRow}>
              <IconButton
                aria-label={t('drawer.textBold')}
                tooltipPlacement="bottom"
                onClick={handleToggleBold}
                className={style.textBold ? 'highlighted active' : ''}
                sx={memoSxClasses.formatButton}
              >
                <FormatBoldIcon />
              </IconButton>
              <IconButton
                aria-label={t('drawer.textItalic')}
                tooltipPlacement="bottom"
                onClick={handleToggleItalic}
                className={style.textItalic ? 'highlighted active' : ''}
                sx={memoSxClasses.formatButton}
              >
                <FormatItalicIcon />
              </IconButton>
            </Box>
          </ListItem>
        </>
      )}

      {/* Fill color - hide for LineString and Text */}
      {currentGeomType !== 'LineString' && currentGeomType !== 'Text' && (
        <ListItem sx={memoSxClasses.listItem}>
          <Typography variant="subtitle2" sx={memoSxClasses.label}>
            {t('drawer.fillColour')}
          </Typography>
          <MuiColorInput value={localFillColor} onChange={handleFillColorChange} onBlur={handleFillColorClose} sx={memoSxClasses.input} />
        </ListItem>
      )}

      {/* Point-specific controls */}
      {currentGeomType === 'Point' && (
        <ListItem sx={memoSxClasses.listItem}>
          <Typography variant="subtitle2" sx={memoSxClasses.label}>
            {t('drawer.iconSize')}
          </Typography>
          <TextField
            value={style.iconSize || 24}
            onChange={handleIconSizeChange}
            sx={memoSxClasses.input}
            slotProps={{
              input: {
                type: 'number',
                inputProps: { min: 8, max: 100, step: 1 },
              },
            }}
          />
        </ListItem>
      )}

      {/* Stroke controls - show for all except Text */}
      {currentGeomType !== 'Text' && (
        <>
          <ListItem sx={memoSxClasses.listItem}>
            <Typography variant="subtitle2" sx={memoSxClasses.label}>
              {t('drawer.strokeColour')}
            </Typography>
            <MuiColorInput
              value={localStrokeColor}
              onChange={handleStrokeColorChange}
              onBlur={handleStrokeColorClose}
              sx={memoSxClasses.input}
            />
          </ListItem>

          <ListItem sx={memoSxClasses.listItem}>
            <Typography variant="subtitle2" sx={memoSxClasses.label}>
              {t('drawer.strokeWidth')}
            </Typography>
            <TextField
              value={style.strokeWidth}
              onChange={handleStrokeWidthChange}
              sx={memoSxClasses.input}
              slotProps={{
                input: {
                  type: 'number',
                  inputProps: { min: 0, max: 100, step: 0.1 },
                },
              }}
            />
          </ListItem>
        </>
      )}
    </List>
  );
}
